import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, getCart, setCart } from "@/lib/api";
import { BrandLogo, RatingStars, OpenBadge, CustomerHeader, EmptyState } from "@/components/tabdine/Shared";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// ============ HEADER (restaurant) ============
const RHeader = ({ r, tableNumber }) => (
  <div className="bg-white border-b border-brand-dark/10">
    <div className="max-w-5xl mx-auto px-4 py-4 flex items-start gap-4">
      <img
        src={r.logo_url}
        alt=""
        className="w-14 h-14 rounded-full object-cover bg-brand-orange/10"
        onError={(e) => (e.currentTarget.style.opacity = 0)}
      />
      <div className="flex-1 min-w-0">
        <h1 className="font-heading font-bold text-xl text-brand-dark truncate">{r.name}</h1>
        <div className="text-sm text-brand-dark/60 truncate">
          {(r.cuisine_type || []).join(" · ")} · {r.opening_time}–{r.closing_time}
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <OpenBadge open={r.is_open && !r.is_closed_today} />
          <button
            className="flex items-center gap-1 text-xs text-brand-dark/70 hover:text-brand-orange"
            onClick={() => document.getElementById("tab-reviews")?.click()}
            data-testid="header-rating-shortcut"
          >
            <RatingStars value={r.avg_rating || 0} />
            <span className="ml-1">{r.avg_rating || 0} ({r.total_reviews || 0})</span>
          </button>
          {tableNumber && (
            <span className="text-xs font-medium bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded-full" data-testid="table-chip">
              Table {tableNumber}
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ============ TAB BAR ============
const TabBar = ({ tabs, activeKey, onSwitch }) => (
  <div className="sticky top-0 z-30 bg-brand-light/95 backdrop-blur border-b border-brand-dark/10 overflow-x-auto hide-scrollbar">
    <div className="max-w-5xl mx-auto flex px-4">
      {tabs.map((t) => (
        <button
          key={t.tab_key}
          id={`tab-${t.tab_key}`}
          data-testid={`tab-${t.tab_key}`}
          onClick={() => onSwitch(t.tab_key)}
          className={`relative px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
            activeKey === t.tab_key
              ? "text-brand-orange"
              : "text-brand-dark/60 hover:text-brand-dark"
          }`}
        >
          {t.tab_label}
          {activeKey === t.tab_key && (
            <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand-orange rounded-full" />
          )}
        </button>
      ))}
    </div>
  </div>
);

// ============ MENU TAB ============
const MenuTab = ({ r, tableId, cart, onAdd, offers, happyHours }) => {
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get(`/restaurants/${r.slug}/menu`).then((res) => {
      setMenu(res.data);
      if (res.data.categories?.[0]) setActiveCat(res.data.categories[0].id);
    });
  }, [r.slug]);

  const itemsByCat = useMemo(() => {
    const m = {};
    menu.items.forEach((i) => {
      (m[i.category_id] ||= []).push(i);
    });
    return m;
  }, [menu]);

  const qtyInCart = (id) => cart.find((c) => c.item_id === id)?.quantity || 0;

  const activeHH = happyHours && happyHours.is_active;
  const hhItemIds = new Set(happyHours?.item_ids || []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-4">
      {activeHH && (
        <div className="mb-3 rounded-xl bg-gradient-to-r from-brand-orange/10 to-amber-100/50 border border-brand-orange/20 p-3 text-sm" data-testid="happy-hours-banner">
          <span className="font-heading font-semibold text-brand-orange">🔥 Happy Hours</span>
          <span className="text-brand-dark/80"> — {happyHours.discount_percent}% off on select items till {happyHours.end_time}</span>
        </div>
      )}
      {offers && offers.length > 0 && (
        <div className="mb-3 rounded-xl bg-brand-dark text-brand-light p-3" data-testid="offer-banner">
          <div className="text-xs uppercase tracking-widest text-brand-orange">Offer</div>
          <div className="font-heading font-semibold">{offers[0].title}</div>
          {offers[0].code && (
            <div className="text-xs mt-0.5 text-brand-light/70">
              Use code <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">{offers[0].code}</span>
            </div>
          )}
        </div>
      )}

      {/* Category nav */}
      <div className="sticky top-[52px] z-20 -mx-4 px-4 py-2 bg-brand-light/95 backdrop-blur flex gap-2 overflow-x-auto hide-scrollbar">
        {menu.categories.map((c) => (
          <button
            key={c.id}
            data-testid={`cat-${c.id}`}
            onClick={() => {
              setActiveCat(c.id);
              document.getElementById(`cat-section-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-sm border ${
              activeCat === c.id ? "bg-brand-dark text-brand-light border-brand-dark" : "bg-white text-brand-dark/70 border-brand-dark/10"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {menu.categories.map((c) => (
        <div key={c.id} id={`cat-section-${c.id}`} className="mt-6">
          <h2 className="font-heading font-semibold text-lg text-brand-dark mb-2">{c.name}</h2>
          <div className="grid gap-3">
            {(itemsByCat[c.id] || []).map((it) => {
              const inCart = qtyInCart(it.id);
              const discounted = activeHH && hhItemIds.has(it.id);
              const shownPrice = discounted ? it.price * (1 - happyHours.discount_percent / 100) : it.price;
              return (
                <div
                  key={it.id}
                  data-testid={`menu-item-${it.id}`}
                  className="bg-white rounded-2xl border border-brand-dark/5 p-3 flex gap-3 items-start"
                >
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden shrink-0 bg-brand-light">
                    {it.video_url ? (
                      <video src={it.video_url} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                    ) : (
                      <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <span
                        className={`inline-block w-3.5 h-3.5 border rounded-sm mt-1.5 shrink-0 ${
                          it.is_veg ? "border-green-600" : "border-red-600"
                        }`}
                      >
                        <span
                          className={`block w-1.5 h-1.5 rounded-full m-auto mt-[3px] ${
                            it.is_veg ? "bg-green-600" : "bg-red-600"
                          }`}
                        />
                      </span>
                      <div className="flex-1">
                        <div className="font-heading font-semibold text-brand-dark leading-tight">{it.name}</div>
                        <div className="text-xs text-brand-dark/60 mt-0.5 line-clamp-2">{it.description}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {(it.tags || []).map((t) => (
                            <span
                              key={t}
                              className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-brand-orange/10 text-brand-orange"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-brand-dark font-heading font-semibold">
                        {inr(shownPrice)}{" "}
                        {discounted && <span className="text-xs line-through text-brand-dark/40 ml-1">{inr(it.price)}</span>}
                      </div>
                      {inCart ? (
                        <div className="flex items-center gap-2 bg-brand-orange/10 rounded-full">
                          <button
                            data-testid={`item-dec-${it.id}`}
                            onClick={() => onAdd(it, -1)}
                            className="w-8 h-8 grid place-items-center text-brand-orange font-bold"
                          >
                            −
                          </button>
                          <span className="text-sm w-4 text-center text-brand-dark font-medium">{inCart}</span>
                          <button
                            data-testid={`item-inc-${it.id}`}
                            onClick={() => onAdd(it, 1)}
                            className="w-8 h-8 grid place-items-center text-brand-orange font-bold"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => onAdd(it, 1)}
                          data-testid={`item-add-${it.id}`}
                          className="px-3 py-1.5 text-sm font-medium rounded-full bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors"
                        >
                          + Add
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ REVIEWS TAB ============
const ReviewsTab = ({ r, tableId }) => {
  const nav = useNavigate();
  const [data, setData] = useState({ reviews: [], distribution: {}, total: 0 });
  const [sort, setSort] = useState("recent");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [eligible, setEligible] = useState(false);

  const load = () => api.get(`/restaurants/${r.slug}/reviews?sort=${sort}`).then((res) => setData(res.data));
  useEffect(() => {
    load();
    // eligible if customer has any served/paid order
    const t = localStorage.getItem("tabdine_customer_session");
    if (!t) return;
    api.get("/customer/orders").then((res) => {
      const hasCompleted = res.data.some((o) => o.restaurant_slug === r.slug && ["served", "paid"].includes(o.status));
      setEligible(hasCompleted);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [sort, r.slug]);

  const submit = async () => {
    if (!localStorage.getItem("tabdine_customer_session")) {
      toast.error("Please login to post a review");
      nav("/login");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/restaurants/${r.slug}/reviews`, { rating, comment });
      setComment("");
      toast.success("Thanks for your review!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post review");
    }
    setBusy(false);
  };

  const dist = data.distribution || {};
  const total = data.total || 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-4">
      {/* Summary */}
      <div className="bg-white rounded-2xl border border-brand-dark/5 p-4 flex flex-col sm:flex-row gap-6 items-center sm:items-start">
        <div className="text-center">
          <div className="font-heading text-5xl font-bold text-brand-dark">{r.avg_rating || 0}</div>
          <RatingStars value={r.avg_rating || 0} size={18} />
          <div className="text-xs text-brand-dark/60 mt-1">{total} reviews</div>
        </div>
        <div className="flex-1 w-full">
          {[5, 4, 3, 2, 1].map((s) => {
            const n = dist[s] || 0;
            const pct = total ? Math.round((n / total) * 100) : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-xs my-1">
                <div className="w-4 text-right">{s}★</div>
                <div className="flex-1 h-1.5 bg-brand-dark/10 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-orange" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-8 text-right text-brand-dark/60">{n}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Leave review */}
      <div className="mt-3 bg-white rounded-2xl border border-brand-dark/5 p-4">
        <div className="font-heading font-semibold text-brand-dark">Leave a Review</div>
        {!eligible ? (
          <div className="text-xs text-brand-dark/60 mt-1">
            Only customers with a completed order can post a review.
          </div>
        ) : (
          <>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  data-testid={`review-star-${i}`}
                  onClick={() => setRating(i)}
                  className="text-2xl"
                  style={{ color: i <= rating ? "#F59E0B" : "#E5E7EB" }}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="How was your visit?"
              className="mt-2 w-full text-sm rounded-lg border border-brand-dark/10 p-2 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
              data-testid="review-comment"
            />
            <div className="mt-2 flex justify-end">
              <Button onClick={submit} disabled={busy} className="bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="review-submit">
                {busy ? "Posting..." : "Post Review"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Sort */}
      <div className="mt-3 flex gap-2 text-xs overflow-x-auto hide-scrollbar">
        {[
          ["recent", "Most Recent"],
          ["highest", "Highest Rated"],
          ["lowest", "Lowest Rated"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSort(k)}
            data-testid={`sort-${k}`}
            className={`shrink-0 px-3 py-1 rounded-full border ${
              sort === k
                ? "bg-brand-dark text-brand-light border-brand-dark"
                : "bg-white text-brand-dark/70 border-brand-dark/10"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Review list */}
      <div className="mt-3 space-y-3">
        {data.reviews.length === 0 && <EmptyState title="No reviews yet" subtitle="Be the first to share how you liked your visit." />}
        {data.reviews.map((rv) => (
          <div key={rv.id} className="bg-white rounded-2xl border border-brand-dark/5 p-4" data-testid={`review-${rv.id}`}>
            {rv.is_pinned && (
              <div className="text-[10px] uppercase tracking-widest text-brand-orange font-bold mb-1">Pinned</div>
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-brand-orange/10 grid place-items-center text-brand-orange font-bold text-sm">
                  {(rv.customer_name || "?")[0]}
                </div>
                <div>
                  <div className="text-sm font-medium text-brand-dark">{rv.customer_name}</div>
                  <RatingStars value={rv.rating} />
                </div>
              </div>
              <div className="text-xs text-brand-dark/50">{(rv.created_at || "").slice(0, 10)}</div>
            </div>
            {rv.comment && <div className="text-sm text-brand-dark/80 mt-2">{rv.comment}</div>}
            {rv.restaurant_reply && (
              <div className="mt-3 ml-6 border-l-2 border-brand-orange/30 pl-3 text-xs">
                <div className="font-medium text-brand-dark">Response from {r.name}</div>
                <div className="text-brand-dark/70 mt-0.5">{rv.restaurant_reply}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ CUSTOM TAB ============
const CustomTab = ({ tab }) => {
  if (tab.tab_type === "custom_html") {
    return (
      <div
        className="max-w-3xl mx-auto px-4 py-6 prose prose-sm text-brand-dark/80"
        dangerouslySetInnerHTML={{ __html: tab.content?.html || "" }}
        data-testid="custom-html"
      />
    );
  }
  if (tab.tab_type === "gallery") {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-3 gap-3">
        {(tab.content?.images || []).map((src, i) => (
          <img key={i} src={src} className="aspect-square object-cover rounded-xl" alt="" />
        ))}
      </div>
    );
  }
  if (tab.tab_type === "link" && tab.content?.external_url) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 text-center">
        <a
          href={tab.content.external_url}
          target="_blank"
          rel="noreferrer"
          className="text-brand-orange underline"
          data-testid="external-link"
        >
          Open external page →
        </a>
      </div>
    );
  }
  return <div className="text-center py-10 text-brand-dark/60">No content configured.</div>;
};

// ============ MAIN ============
export default function RestaurantPage() {
  const [params, setParams] = useSearchParams();
  const slug = params.get("r");
  const tab = params.get("tab") || "menu";
  const tableId = params.get("table");
  const nav = useNavigate();

  const [r, setR] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [cart, setCartState] = useState([]);
  const [table, setTable] = useState(null);
  const [offers, setOffers] = useState([]);
  const [happyHours, setHappyHours] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api.get(`/restaurants/${slug}`).then((res) => setR(res.data));
    api.get(`/restaurants/${slug}/page-tabs`).then((res) => setTabs(res.data));
    api.get(`/restaurants/${slug}/offers`).then((res) => setOffers(res.data));
    api.get(`/restaurants/${slug}/happy-hours`).then((res) => setHappyHours(res.data));
    setCartState(getCart(slug));
    if (tableId) {
      api.get(`/tables/${tableId}`).then((res) => setTable(res.data)).catch(() => {});
      api.post("/tap", { restaurant_id: null, table_id: tableId, source: "qr" }).catch(() => {});
    }
  }, [slug, tableId]);

  const onAdd = (item, delta) => {
    const existing = cart.find((c) => c.item_id === item.id);
    let next;
    if (existing) {
      const newQty = existing.quantity + delta;
      if (newQty <= 0) next = cart.filter((c) => c.item_id !== item.id);
      else next = cart.map((c) => (c.item_id === item.id ? { ...c, quantity: newQty } : c));
    } else if (delta > 0) {
      next = [...cart, { item_id: item.id, item_name: item.name, item_price: item.price, image_url: item.image_url, quantity: delta }];
      toast.success(`${item.name} added`);
    } else return;
    setCartState(next);
    setCart(slug, next);
  };

  if (!r)
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-brand-dark/60">Loading…</div>
      </div>
    );

  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);
  const cartTotal = cart.reduce((s, c) => s + c.quantity * c.item_price, 0);

  return (
    <div className="min-h-screen bg-brand-light pb-24">
      {/* Top link back */}
      <div className="bg-white/70">
        <div className="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between">
          <button onClick={() => nav("/")} className="flex items-center gap-1 text-sm text-brand-dark/70 hover:text-brand-orange" data-testid="back-home">
            <ArrowLeft size={16} /> All restaurants
          </button>
          <BrandLogo small onClick={() => nav("/")} />
        </div>
      </div>
      <RHeader r={r} tableNumber={table?.table_number} />
      <TabBar tabs={tabs} activeKey={tab} onSwitch={(k) => setParams({ r: slug, tab: k, ...(tableId ? { table: tableId } : {}) })} />

      <div>
        {tab === "menu" && (
          <MenuTab r={r} tableId={tableId} cart={cart} onAdd={onAdd} offers={offers} happyHours={happyHours} />
        )}
        {tab === "reviews" && <ReviewsTab r={r} tableId={tableId} />}
        {!["menu", "reviews"].includes(tab) &&
          (() => {
            const t = tabs.find((x) => x.tab_key === tab);
            if (!t) return <EmptyState title="Tab not found" />;
            return <CustomTab tab={t} />;
          })()}
      </div>

      {/* Cart FAB */}
      {cartCount > 0 && r.phase2_enabled && (
        <div className="fixed bottom-4 inset-x-4 z-40 max-w-md mx-auto pulse-once">
          <button
            onClick={() => nav(`/cart?r=${slug}${tableId ? `&table=${tableId}` : ""}`)}
            className="w-full flex items-center justify-between bg-brand-orange text-white rounded-full px-5 py-3 shadow-xl hover:bg-brand-orange-dark transition-colors"
            data-testid="cart-fab"
          >
            <span className="flex items-center gap-2 font-medium">
              <ShoppingBag size={16} /> {cartCount} item{cartCount > 1 ? "s" : ""}
            </span>
            <span className="font-heading font-semibold">{inr(cartTotal)} · View Cart →</span>
          </button>
        </div>
      )}
    </div>
  );
}
